import { EchartsWithTheme, Empty, useTheme } from '@maxtropy/components';
import React, { useEffect, useMemo, useState } from 'react';
import tittleDecoration from '../tittleDecoration.png';
import ResizeObserver from 'rc-resize-observer';
import { Spin } from 'antd';
import styles from './index.module.scss';
import { EnergyMonitorContext } from '../';
import { isEmpty } from 'lodash-es';
import dayjs from 'dayjs';
import {
  apiV2HighEnergyDeviceRunningStateStatisticsPost,
  V2HighEnergyDeviceRunningStateStatisticsPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { DateType } from '../utils';
import useLatestRequest from '../useLatestRequest';

interface RunStateProps {
  timeResolution?: string;
  displayValue?: any;
  timeRangeTidy?: number[];
  dataQueryPeriodId?: number;
  dataQueryPeriodValue?: string;
}
const RunState: React.FC<RunStateProps> = ({
  timeResolution,
  timeRangeTidy,
  dataQueryPeriodId,
  dataQueryPeriodValue,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const { deviceLedgerId } = React.useContext(EnergyMonitorContext);
  const [datas, setDatas] = useState<V2HighEnergyDeviceRunningStateStatisticsPostResponse>();
  const { runLatest, cancelLatest } = useLatestRequest();
  useEffect(() => {
    if (!deviceLedgerId || !timeRangeTidy) {
      cancelLatest();
      setDatas(undefined);
      setLoading(false);
      return;
    }
    setLoading(true);
    runLatest(
      () =>
        apiV2HighEnergyDeviceRunningStateStatisticsPost({
          deviceLedgerId,
          timeResolution,
          startTime: timeRangeTidy[0],
          endTime: timeRangeTidy[1],
          dataQueryPeriodId,
        }),
      {
        onSuccess: setDatas,
        onSettled: () => setLoading(false),
      }
    );
  }, [cancelLatest, dataQueryPeriodId, deviceLedgerId, runLatest, timeRangeTidy, timeResolution]);

  const xLabel = useMemo(() => {
    if (timeResolution === DateType.MONTH || timeResolution === DateType.WEEK) {
      return 'YYYY-MM-DD';
    } else if (timeResolution === DateType.YEAR) {
      return 'YYYY-MM';
    } else {
      return 'HH:mm';
    }
  }, [timeResolution]);

  const getChartOption = () => {
    return {
      color: ['#16DD8E', '#FFCB47', '#FF4D4F', '#2D8DFF'],
      grid: {
        left: 40,
        right: 40,
        top: 60,
        containLabel: true,
      },
      tooltip: {
        formatter: (tooltipData: any) => {
          return `
             <div style='width:170px'>
              <div style='color: rgba(255,255,255,0.65)'>${dayjs(Number(tooltipData[0]?.axisValue)).format(
                timeResolution === DateType.DAY ? 'YYYY-MM-DD HH:mm' : xLabel
              )}</div>
              <div>${tooltipData
                ?.map(
                  (item: any) =>
                    `<div style='width: 174px;display: flex;align-items: center;margin-bottom: 4px;'><span style='display:inline-block;width:8px;height:8px;background:${
                      item?.color
                    };border-radius:50%;margin-right:6px'></span> ${
                      item.seriesName ?? '--'
                    }<span style='flex:1;text-align: right'>${item.value ?? '--'}${
                      item.seriesName !== '设备使用率' ? 'h' : '%'
                    }</span></div></div>`
                )
                .join('')}</div>
             </div>
            `;
        },
      },
      legend: [
        {
          top: 6,
          right: 10,
          bottom: 8,
          icon: 'rect',
          itemWidth: 16,
          itemHeight: 4,
          itemGap: 16,
          padding: [0, 8, 0, 0],
          data: [
            { name: '设备使用率' }, // 折线
          ],
        },
        {
          top: 6,
          right: 107,
          bottom: 8,
          icon: 'rect',
          itemWidth: 12,
          itemHeight: 12,
          itemGap: 16,
          padding: [0, 8, 0, 0],
          data: [{ name: '运行时长' }, { name: '待机时长' }, { name: '停机时长' }],
        },
      ],
      xAxis: [
        {
          type: 'category',
          axisTick: {
            alignWithLabel: true,
          },
          data: datas?.stateData?.map(i => i.time),
          axisLabel: {
            formatter: (value: number) => {
              return dayjs(Number(value)).format(xLabel);
            },
            margin: 8,
            lineHeight: 14,
          },
        },
      ],
      dataZoom: {
        type: 'slider',
        bottom: 20,
        height: 20,
        labelFormatter: (value: string) => {
          const time = datas?.stateData?.[Number(value)]?.time;
          return dayjs(time).format(xLabel);
        },
      },
      yAxis: [
        {
          type: 'value',
          name: `时长(${datas?.unitName})`,
          alignTicks: true,
          axisLine: {},
        },
        {
          type: 'value',
          name: '使用率(%)',
          nameGap: 8,
          axisLabel: {
            margin: 8,
            lineHeight: 20,
          },
        },
      ],
      series: [
        {
          name: '运行时长',
          type: 'bar',
          barMaxWidth: 80,
          yAxisIndex: 0,
          showSymbol: false,
          data: datas?.stateData?.map(i => i.runningTime),
        },
        {
          name: '待机时长',
          type: 'bar',
          barMaxWidth: 80,
          showSymbol: false,
          data: datas?.stateData?.map(i => i.standbyTime),
        },
        {
          name: '停机时长',
          type: 'bar',
          barMaxWidth: 80,
          showSymbol: false,
          data: datas?.stateData?.map(i => i.shutdownTime),
        },
        {
          name: '设备使用率',
          smooth: true,
          type: 'line',
          yAxisIndex: 1,
          showSymbol: false,
          data: datas?.stateData?.map(i => {
            const val = i.runningTimeRatio;
            if (val === 0) return '0';
            if (val === undefined || val === null) return '--';
            const percent = val * 100;
            if (percent < 0.001) return '0.001';
            return percent.toFixed(3);
          }),
        },
      ],
    };
  };
  return (
    <div className={styles.container}>
      <div className={styles.headerWrapper}>
        <div>
          <img src={tittleDecoration} alt="pic" className={styles.titleBorder} />
          <span className={styles.title}>运行状态统计{dataQueryPeriodValue && `(${dataQueryPeriodValue})`}</span>
        </div>
      </div>
      <div className={styles.content_box}>
        <div className={styles.chart_box}>
          <ResizeObserver>
            <Spin spinning={loading}>
              <EchartsWithTheme
                empty={{
                  isShowEmpty: isEmpty(datas),
                  emptyNode: <Empty style={{ height: 320, padding: '80px' }} />,
                }}
                style={{
                  height: 320,
                }}
                option={getChartOption()}
                theme={theme}
              />
            </Spin>
          </ResizeObserver>
        </div>
      </div>
    </div>
  );
};

export default RunState;
